import React from 'react';
import SignOutIcon from 'components/icons/SignOutIcon';
import SpeechBalloonIcon from 'components/icons/SpeechBalloonIcon';
import CalendarIcon from 'components/icons/CalendarIcon';
import LocalizedLink from 'components/LocalizedLink';
import classnames from 'classnames';
import { Translator } from 'Translator';
import FavoritesIcon from 'components/icons/FavoritesIcon';
import ProfileIcon from 'components/icons/ProfileIcon';
import LockIconOutline from 'components/icons/LockIconOutline';
import { LocationDescriptor } from "history";
import { signOut } from 'modules/authentication/helpers/nextauth';
import { useAccount, useUser } from '~/authentication/store';
import ExternalLink from 'components/organisms/ExternalLink';
import HomeIcon from "components/icons/HomeIcon";

const CSS = require('./NavbarList.less');

interface ListItemOuterProps {
  onClick: () => void;
  onProfileClick?: () => void;
  onLogoutClick?: (redirectUrl: LocationDescriptor) => void;
  onBecomeYourself?: () => void;
  isMobile?: boolean;
}

function ProfileListItem({ onClick, onProfileClick }: ListItemOuterProps) {
  const { isHost, isLoggedIn } = useUser();
  if (!isLoggedIn) {
    return null;
  }
  if (isHost) {
    return (
      <li className={classnames(CSS.MenuItem)}>
        <LocalizedLink className={CSS.MenuItemButton} to={'/host/profile/'}>
          {Translator.translate('Navbar.Buttons.editMyProfile', {}, true, 'Edit profile')}
        </LocalizedLink>
        <div className={CSS.IconContainer}>
          <ProfileIcon className={CSS.Icon} width={24} height={24} />
        </div>
      </li>
    );
  }
  return (
    <li className={CSS.MenuItem} onClick={onClick}>
      <button className={CSS.MenuItemButton} onClick={onProfileClick}>
        {Translator.translate('Navbar.Buttons.account', {}, true, 'Account')}
      </button>
      <div className={CSS.IconContainer}>
        <ProfileIcon className={CSS.Icon} width={24} height={24} />
      </div>
    </li>
  );
}

function FavoritesListItem({ onClick }: ListItemOuterProps) {
  const { isLoggedIn } = useUser();
  if (!isLoggedIn) {
    return null;
  }
  return (
    <li className={classnames(CSS.MenuItem)} onClick={onClick}>
      <LocalizedLink className={CSS.MenuItemButton} to={'/favorites/'}>
        {Translator.translate('Navbar.Buttons.favorites', {}, true, 'Favorites')}
      </LocalizedLink>
      <div className={CSS.IconContainer}>
        <FavoritesIcon className={CSS.Icon} width={22} height={22} />
      </div>
    </li>
  );
}

function SignOutListItem({ onClick }: ListItemOuterProps) {
  const { isLoggedIn } = useUser();
  if (!isLoggedIn) {
    return null;
  }

  const onLogout = () => {
    void signOut(window.location.href);
  }

  return (
    <li className={CSS.MenuItem} onClick={onClick}>
      <button className={CSS.MenuItemButton}
        onClick={onLogout}>
        {Translator.translate('Navbar.Buttons.signOut', {}, true, 'Sign out')}
      </button>
      <div className={CSS.IconContainer}>
        <SignOutIcon className={CSS.Icon} width={21} height={22} />
      </div>
    </li>
  );
}

function PasswordChangeListItem({ onClick }: ListItemOuterProps) {
  const { isLoggedIn } = useUser();
  if (!isLoggedIn) {
    return null;
  }
  return (
    <li className={CSS.MenuItem} onClick={onClick}>
      <ExternalLink elementType={'a'} className={CSS.MenuItemLink} url={`${URL_STOREFRONT_HOST}/password/`}>
        {Translator.translate('Navbar.Buttons.changePassword', {}, true, 'Change password')}
      </ExternalLink>
      <div className={CSS.IconContainer}>
        <LockIconOutline className={CSS.Icon} width={19} height={23} />
      </div>
    </li>
  );
}

function BecomeYourselfListItem({ onClick, onBecomeYourself }: ListItemOuterProps) {
  const { account, realAccount } = useAccount();
  const { isLoggedIn } = useUser();
  if (!isLoggedIn) {
    return null;
  }
  const isSomeoneElse = account && realAccount && account.id !== realAccount.id;
  if (!isSomeoneElse) {
    return null;
  }
  return (
    <li className={classnames(CSS.MenuItem, CSS.HighlightedItem)} onClick={onClick}>
      <button className={CSS.MenuItemButton} onClick={onBecomeYourself}>
        Become yourself again
      </button>
      <div className={CSS.IconContainer} />
    </li>
  );
}

function MessagesListItem({ onClick, isMobile }: ListItemOuterProps) {
  const { isLoggedIn } = useUser();
  if (!isLoggedIn || !isMobile) {
    return null;
  }
  return (
    <li className={classnames(CSS.MenuItem)} onClick={onClick}>
      <LocalizedLink className={CSS.MenuItemButton} to={'/messages/'}>
        {Translator.translate('Navbar.Buttons.messages', {}, true, 'Messages')}
      </LocalizedLink>
      <div className={CSS.IconContainer}>
        <SpeechBalloonIcon className={CSS.Icon} />
      </div>
    </li>
  );
}

function BookingsListItem({ onClick, isMobile }: ListItemOuterProps) {
  const { isLoggedIn } = useUser();
  if (!isLoggedIn || !isMobile) {
    return null;
  }
  return (
    <li className={CSS.MenuItem} onClick={onClick}>
      <LocalizedLink className={CSS.MenuItemButton} to={'/schedule/'}>
        {Translator.translate('Navbar.Buttons.bookings', {}, true, 'Bookings')}
      </LocalizedLink>
      <div className={CSS.IconContainer}>
        <CalendarIcon className={CSS.Icon} />
      </div>
    </li>
  );
}

function OnboardingListItem({ onClick }: ListItemOuterProps) {
  return (
    <li className={CSS.MenuItem} onClick={onClick}>
      <LocalizedLink className={CSS.MenuItemButton} to={'/host/onboarding/'}>
        Onboarding
      </LocalizedLink>
      <div className={CSS.IconContainer}>
        <HomeIcon className={CSS.Icon}/>
      </div>
    </li>
  );
}

export {
  FavoritesListItem,
  ProfileListItem,
  SignOutListItem,
  PasswordChangeListItem,
  BecomeYourselfListItem,
  MessagesListItem,
  BookingsListItem,
  OnboardingListItem
};
